/**
 * Scene loader plugin for .real format (Adobe Aero).
 *
 * .real files are ZIP archives containing:
 * - SCENE_JSON: Aero scene definition
 * - Assets/: Folder with GLB and other asset files
 *
 * Transform handling:
 * Aero flattens/collapses GLB internal transforms into scene transforms.
 * The scene scale represents the final desired visual size. However, Trace
 * preserves GLB internal hierarchies including any baked root node scales.
 * To match Aero's visual result, the plugin reads each GLB's root node
 * scale at parse time and divides the scene scale by it:
 *   correctedScale = aeroSceneScale / glbRootScale
 */

import JSZip from 'jszip';
import { BaseTracePlugin, ParsedSceneStructure, ParsedSceneNode } from './BaseTracePlugin';
import type { ISceneLoaderPluginExtensions } from "@babylonjs/core/Loading/sceneLoader";
import type { AeroSceneFile, AeroNode3D, AeroAssetReference } from '../aerodata';
import { Utility } from '../../utils/Utility';

// =============================================================================
// Constants
// =============================================================================

const AERO_SCENE_FILE_NAME = 'SCENE_JSON';

/** Baked transform found in a GLB's root node chain */
interface GlbRootTransform {
    scale?: [number, number, number];
    /** Quaternion [x, y, z, w] */
    rotation?: [number, number, number, number];
}

/** Minimal glTF JSON structure for reading root node transforms */
interface GltfJson {
    scene?: number;
    scenes?: { nodes?: number[] }[];
    nodes?: {
        scale?: [number, number, number];
        rotation?: [number, number, number, number];
        children?: number[];
    }[];
}

// =============================================================================
// Plugin Implementation
// =============================================================================

export class AeroSceneLoaderPlugin extends BaseTracePlugin {
    readonly name: string = "aero";
    readonly extensions: ISceneLoaderPluginExtensions = {
        ".real": { isBinary: true },
    };

    /**
     * Parse .real ZIP archive into common scene structure
     */
    protected async parseSceneData(
        data: unknown,
        rootUrl: string,
        _fileName?: string
    ): Promise<ParsedSceneStructure> {
        // Load ZIP archive
        const zip = await JSZip.loadAsync(data as ArrayBuffer);

        // Read SCENE_JSON
        const sceneFile = zip.file(AERO_SCENE_FILE_NAME);
        if (!sceneFile) {
            throw new Error('Archive does not contain SCENE_JSON');
        }
        const aeroData = JSON.parse(await sceneFile.async('string')) as AeroSceneFile;

        // Extract assets to blobs for ResourceManager storage
        const assetBlobs = await this.extractAssets(zip);

        // Read baked GLB root transforms for correction
        const glbRootTransforms = await this.readGlbRootTransforms(assetBlobs);

        // Parse Aero scene to nodes (with transform correction)
        const nodes = this.parseAeroScene(aeroData, glbRootTransforms);

        return {
            nodes,
            assetBlobs,
            rootUrl,
        };
    }

    /**
     * Extract assets from ZIP to blobs for ResourceManager storage.
     * Aero uses "Assets/" folder (capital A).
     */
    private async extractAssets(zip: JSZip): Promise<Map<string, Blob>> {
        const assetBlobs = new Map<string, Blob>();
        const assetsFolder = zip.folder('Assets');

        if (assetsFolder) {
            const assetFiles = assetsFolder.filter((_relativePath, file) => !file.dir);
            for (const file of assetFiles) {
                const arrayBuffer = await file.async('arraybuffer');
                // Extract just the filename (remove 'Assets/' prefix)
                const assetFilename = file.name.replace(/^Assets\//i, '');
                const mimeType = Utility.mimeTypeFromFilename(assetFilename);
                const blob = new Blob([arrayBuffer], mimeType ? { type: mimeType } : undefined);

                // Store blob for ResourceManager (keyed by path like "assets/filename.glb")
                // Normalize to lowercase 'assets/' for consistency
                assetBlobs.set(`assets/${assetFilename}`, blob);
            }
        }

        return assetBlobs;
    }

    /**
     * Read the baked root node transform from each GLB asset.
     *
     * GLB format: 12-byte header, then chunks. The first chunk is JSON containing
     * the glTF scene graph. We parse it to find non-identity transforms that Aero
     * baked into the GLB when flattening the hierarchy.
     *
     * Walks down single-child chains to find the first node with a non-identity
     * transform (scale or rotation). Returns a map of asset path → transform.
     */
    private async readGlbRootTransforms(
        assetBlobs: Map<string, Blob>
    ): Promise<Map<string, GlbRootTransform>> {
        const transforms = new Map<string, GlbRootTransform>();

        for (const [path, blob] of assetBlobs) {
            if (!path.endsWith('.glb')) continue;

            try {
                const buffer = await blob.arrayBuffer();
                const view = new DataView(buffer);

                // GLB header: magic (4) + version (4) + length (4) = 12 bytes
                // First chunk: length (4) + type (4) + data (length bytes)
                if (buffer.byteLength < 20) continue;

                const chunkLength = view.getUint32(12, true);
                const chunkType = view.getUint32(16, true);

                // 0x4E4F534A = "JSON" in little-endian
                if (chunkType !== 0x4E4F534A) continue;

                const jsonBytes = new Uint8Array(buffer, 20, chunkLength);
                const jsonStr = new TextDecoder().decode(jsonBytes);
                const gltf = JSON.parse(jsonStr) as GltfJson;

                // Find the root node: scene → nodes[0] (first root node)
                const sceneIndex = gltf.scene ?? 0;
                const scene = gltf.scenes?.[sceneIndex];
                if (!scene?.nodes?.length) continue;

                // Walk down single-child chains to find baked transforms
                let nodeIndex = scene.nodes[0];
                while (nodeIndex !== undefined) {
                    const node = gltf.nodes?.[nodeIndex];
                    if (!node) break;

                    const result: GlbRootTransform = {};
                    let hasTransform = false;

                    // Check scale
                    if (node.scale) {
                        const [sx, sy, sz] = node.scale;
                        if (Math.abs(sx - 1) > 0.001 || Math.abs(sy - 1) > 0.001 || Math.abs(sz - 1) > 0.001) {
                            result.scale = [sx, sy, sz];
                            hasTransform = true;
                        }
                    }

                    // Check rotation (glTF stores as quaternion [x, y, z, w])
                    if (node.rotation) {
                        const [rx, ry, rz, rw] = node.rotation;
                        if (Math.abs(rx) > 0.001 || Math.abs(ry) > 0.001 || Math.abs(rz) > 0.001 || Math.abs(rw - 1) > 0.001) {
                            result.rotation = [rx, ry, rz, rw];
                            hasTransform = true;
                        }
                    }

                    if (hasTransform) {
                        transforms.set(path, result);
                        break;
                    }

                    // Continue down single-child chain
                    if (node.children?.length === 1) {
                        nodeIndex = node.children[0];
                    } else {
                        break;
                    }
                }
            } catch {
                // Skip assets we can't parse
            }
        }

        return transforms;
    }

    /**
     * Parse Adobe Aero SCENE_JSON into ParsedSceneNode array
     */
    private parseAeroScene(
        aeroData: AeroSceneFile,
        glbRootTransforms: Map<string, GlbRootTransform>
    ): ParsedSceneNode[] {
        const assetLibrary = aeroData.model.properties.assetLibrary;
        const sceneGraph = aeroData.model.properties.sceneGraph;

        // Build asset reference lookup map
        const assetMap = new Map<string, AeroAssetReference>();
        if (assetLibrary.properties.assetReferences) {
            for (const asset of assetLibrary.properties.assetReferences) {
                assetMap.set(asset.id, asset);
            }
        }

        const nodes: ParsedSceneNode[] = [];

        // Process all scenes in the scene graph
        for (const scene of sceneGraph.properties.scenes) {
            const rootNode = scene.properties.rootNode;
            this.processNode(rootNode, assetMap, glbRootTransforms, nodes, undefined);
        }

        return nodes;
    }

    /**
     * Recursively process an Aero node and its children
     */
    private processNode(
        node: AeroNode3D,
        assetMap: Map<string, AeroAssetReference>,
        glbRootTransforms: Map<string, GlbRootTransform>,
        nodes: ParsedSceneNode[],
        parentName?: string
    ): void {
        const nodeName = node.properties.name || node.id;

        // Convert Aero transform
        const transform = this.convertTransform(node);

        // Find the asset source if this node has a geometry component
        // Store as src path for Model behavior to load from ResourceManager
        let src: string | undefined;
        if (node.properties.components) {
            for (const component of node.properties.components) {
                if (component.class === 'aero::FileBasedGeometryComponent' &&
                    component.properties.assetReference) {
                    const refId = component.properties.assetReference.$reference;
                    const asset = assetMap.get(refId);
                    if (asset) {
                        // Get filename from Aero path (Assets/file.glb) and convert to ResourceManager path
                        const aeroPath = asset.properties.relativePath;
                        const filename = aeroPath.replace(/^Assets\//i, '');
                        // Use lowercase 'assets/' to match ResourceManager storage
                        src = `assets/${filename}`;
                    }
                }
            }
        }

        // Apply Aero transform correction. Aero flattens GLB hierarchies and
        // bakes the internal transforms into the scene transform. Since Trace
        // preserves the GLB's internal transforms, we must compensate:
        //   correctedScale = aeroScale / glbScale
        //   correctedRotation = aeroRotation × inverse(glbRotation)
        if (src) {
            const glbTransform = glbRootTransforms.get(src);
            if (glbTransform) {
                // Scale correction
                if (transform.scale && glbTransform.scale) {
                    const [gx, gy, gz] = glbTransform.scale;
                    transform.scale = [
                        gx !== 0 ? transform.scale[0] / gx : transform.scale[0],
                        gy !== 0 ? transform.scale[1] / gy : transform.scale[1],
                        gz !== 0 ? transform.scale[2] / gz : transform.scale[2],
                    ];
                }

                // Rotation correction
                if (transform.rotation && glbTransform.rotation) {
                    transform.rotation = multiplyQuatByInverse(
                        transform.rotation,
                        glbTransform.rotation
                    );
                }
            }
        }

        // Create node
        const parsedNode: ParsedSceneNode = {
            name: nodeName,
            src,
            transform: (transform.translation || transform.rotation || transform.scale)
                ? transform
                : undefined,
            parentName,
            active: true,
        };

        nodes.push(parsedNode);

        // Recursively process children
        if (node.properties.children) {
            for (const child of node.properties.children) {
                this.processNode(child, assetMap, glbRootTransforms, nodes, nodeName);
            }
        }
    }

    /**
     * Convert Aero transform to ParsedSceneNode transform format
     */
    private convertTransform(node: AeroNode3D): ParsedSceneNode['transform'] & {} {
        const transform: NonNullable<ParsedSceneNode['transform']> = {};

        const localTransform = node.properties.localTransform;
        if (localTransform) {
            if (localTransform.position) {
                transform.translation = localTransform.position;
            }

            if (localTransform.rotation) {
                // Convert Euler angles (degrees) to quaternion
                const [x, y, z] = localTransform.rotation;
                transform.rotation = this.eulerToQuaternion(
                    x * Math.PI / 180,
                    y * Math.PI / 180,
                    z * Math.PI / 180
                );
            }

            if (localTransform.scale) {
                transform.scale = localTransform.scale;
            }
        }

        return transform;
    }

    /**
     * Convert Euler angles to quaternion using extrinsic XYZ convention.
     * Adobe Aero uses extrinsic XYZ Euler angles.
     */
    private eulerToQuaternion(x: number, y: number, z: number): [number, number, number, number] {
        const cx = Math.cos(x);
        const sx = Math.sin(x);
        const cy = Math.cos(y);
        const sy = Math.sin(y);
        const cz = Math.cos(z);
        const sz = Math.sin(z);

        // Rx @ Ry @ Rz rotation matrix
        const m00 = cy * cz;
        const m01 = -cy * sz;
        const m02 = sy;
        const m10 = sx * sy * cz + cx * sz;
        const m11 = -sx * sy * sz + cx * cz;
        const m12 = -sx * cy;
        const m20 = -cx * sy * cz + sx * sz;
        const m21 = cx * sy * sz + sx * cz;
        const m22 = cx * cy;

        // Convert rotation matrix to quaternion
        const trace = m00 + m11 + m22;

        let qx: number, qy: number, qz: number, qw: number;

        if (trace > 0) {
            const s = 0.5 / Math.sqrt(trace + 1.0);
            qw = 0.25 / s;
            qx = (m21 - m12) * s;
            qy = (m02 - m20) * s;
            qz = (m10 - m01) * s;
        } else if (m00 > m11 && m00 > m22) {
            const s = 2.0 * Math.sqrt(1.0 + m00 - m11 - m22);
            qw = (m21 - m12) / s;
            qx = 0.25 * s;
            qy = (m01 + m10) / s;
            qz = (m02 + m20) / s;
        } else if (m11 > m22) {
            const s = 2.0 * Math.sqrt(1.0 + m11 - m00 - m22);
            qw = (m02 - m20) / s;
            qx = (m01 + m10) / s;
            qy = 0.25 * s;
            qz = (m12 + m21) / s;
        } else {
            const s = 2.0 * Math.sqrt(1.0 + m22 - m00 - m11);
            qw = (m10 - m01) / s;
            qx = (m02 + m20) / s;
            qy = (m12 + m21) / s;
            qz = 0.25 * s;
        }

        return [qx, qy, qz, qw];
    }
}

// =============================================================================
// Quaternion Helpers
// =============================================================================

/**
 * Compute q × inverse(r) for quaternions stored as [x, y, z, w].
 * Used to remove a baked GLB rotation from the Aero scene rotation:
 *   correctedRotation = aeroRotation × inverse(glbRotation)
 */
function multiplyQuatByInverse(
    q: [number, number, number, number],
    r: [number, number, number, number]
): [number, number, number, number] {
    // Conjugate of r (inverse for unit quaternions): [-rx, -ry, -rz, rw]
    const irx = -r[0], iry = -r[1], irz = -r[2], irw = r[3];

    // Hamilton product: q × conjugate(r)
    const [qx, qy, qz, qw] = q;
    return [
        qw * irx + qx * irw + qy * irz - qz * iry,
        qw * iry - qx * irz + qy * irw + qz * irx,
        qw * irz + qx * iry - qy * irx + qz * irw,
        qw * irw - qx * irx - qy * iry - qz * irz,
    ];
}
